import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import Breadcrumbs from '@/components/common/Breadcrumbs';
import StatusBadge from '@/components/common/StatusBadge';
import { getLiveBuildings } from '@/services/ncbPublicService';

const { FiBox } = FiIcons;

const BuildingList = () => {
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getLiveBuildings()
      .then(setBuildings)
      .catch(e => {
        console.error('NCB Read Error:', e);
        setError('Unable to load live building records from NoCodeBackend.');
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="p-12 text-center text-slate-500 animate-pulse">
          Loading live building records…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="p-12 text-center text-rose-500 bg-rose-50 rounded-xl border border-rose-200">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumbs items={[{ label: 'Accommodation' }]} />
      
      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Our Properties</h1>
          <p className="text-slate-600 mt-2">Available buildings in the Cloud's Boarding House system.</p>
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 text-xs font-semibold rounded-full border border-green-200">
          <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
          Live NoCodeBackend data
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {buildings.map(b => (
          <div key={b.id} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="bg-sky-100 p-3 rounded-lg text-sky-600">
                <SafeIcon icon={FiBox} className="w-6 h-6" />
              </div>
              <StatusBadge status={b.status} />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">{b.name}</h2>
            <p className="text-sm text-slate-500 mb-6 line-clamp-2">{b.description}</p>
            
            <div className="space-y-3 mb-2">
              <div className="flex justify-between text-sm py-1 border-b border-slate-50">
                <span className="text-slate-500">Floors</span>
                <span className="font-semibold text-slate-700">{b.floor_count}</span>
              </div>
              <div className="flex justify-between text-sm py-1 border-b border-slate-50">
                <span className="text-slate-500">Rooms</span>
                <span className="font-semibold text-slate-700">{b.room_count}</span>
              </div>
              <div className="flex justify-between text-sm py-1 border-b border-slate-50">
                <span className="text-slate-500">Status</span>
                <span className="font-semibold text-slate-700">{b.status}</span>
              </div>
              <div className="flex justify-between text-sm pt-2">
                <span className="text-slate-500">Monthly Rate</span>
                <span className="font-bold text-blue-600">
                  {new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(b.monthly_rate)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {buildings.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200 text-slate-500">
          No building records are currently available.
        </div>
      )}
    </div>
  );
};

export default BuildingList;